import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useDeleteManualCategory,
  useManualCategories,
  useSaveManualCategory
} from '../../api/queries'
import type { ManualCategory } from '../../api/types'
import { useAuth } from '../../auth/AuthProvider'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'

/**
 * Hylderne i manualbiblioteket (docs/DECISIONS.md beslutning 067).
 *
 * <b>At oprette en hylde kan gøres direkte i editoren</b> - det er dér, man
 * opdager, at den mangler. Siden her er til det andet: at omdøbe, sortere og
 * rydde op, og det rører ved andres manualer, så den kræver Administrator.
 *
 * <b>En hylde kan fjernes, uden at manualerne følger med.</b> De havner "uden
 * hylde" og kan sættes på plads igen. En kategori er en måde at ordne tingene
 * på - ikke noget, indholdet hænger i.
 */
export function ManualCategoriesPage() {
  const { isAdmin } = useAuth()
  const { data, isLoading, error } = useManualCategories()

  const save = useSaveManualCategory()
  const remove = useDeleteManualCategory()

  const [editing, setEditing] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  const [adding, setAdding] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  async function rename(category: ManualCategory) {
    const name = draft.trim()
    if (!name || name === category.name) {
      setEditing(null)
      return
    }

    setProblem(null)
    try {
      await save.mutateAsync({
        id: category.id,
        input: { name, sortOrder: category.sortOrder, isActive: category.isActive }
      })
      setEditing(null)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Hylden kunne ikke gemmes.')
    }
  }

  /**
   * Flytter en hylde op eller ned ved at bytte rækkefølgetal med naboen.
   * Rækkefølgen er den, biblioteket lister efter.
   */
  async function move(category: ManualCategory, retning: -1 | 1) {
    const list = [...(data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'da'))
    const index = list.findIndex((c) => c.id === category.id)
    const neighbour = list[index + retning]
    if (!neighbour) return

    setProblem(null)
    try {
      await save.mutateAsync({
        id: category.id,
        input: { name: category.name, sortOrder: neighbour.sortOrder, isActive: category.isActive }
      })
      await save.mutateAsync({
        id: neighbour.id,
        input: { name: neighbour.name, sortOrder: category.sortOrder, isActive: neighbour.isActive }
      })
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Rækkefølgen kunne ikke gemmes.')
    }
  }

  async function toggleActive(category: ManualCategory) {
    setProblem(null)
    try {
      await save.mutateAsync({
        id: category.id,
        input: { name: category.name, sortOrder: category.sortOrder, isActive: !category.isActive }
      })
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Hylden kunne ikke gemmes.')
    }
  }

  async function drop(category: ManualCategory) {
    const advarsel = category.manualCount > 0
      ? `Fjern hylden "${category.name}"? De ${category.manualCount} manualer på den bliver stående - de kommer bare til at stå uden hylde.`
      : `Fjern hylden "${category.name}"?`

    if (!window.confirm(advarsel)) return

    setProblem(null)
    try {
      await remove.mutateAsync(category.id)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Hylden kunne ikke fjernes.')
    }
  }

  async function create() {
    const name = (adding ?? '').trim()
    if (!name) return

    setProblem(null)
    try {
      const next = Math.max(0, ...(data ?? []).map((c) => c.sortOrder)) + 1
      await save.mutateAsync({ input: { name, sortOrder: next, isActive: true } })
      setAdding(null)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Hylden kunne ikke oprettes.')
    }
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const list = [...(data ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'da')
  )

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Manualhylder
        </h1>
        <Link className="btn btn--sm" to="/manuals">
          Til manualerne
        </Link>
      </div>

      <Card>
        <p className="small muted" style={{ marginTop: 0 }}>
          Hylderne er den rækkefølge, manualbiblioteket lister efter. En ny hylde kan også laves
          direkte, mens man skriver en manual. <strong>Fjerner du en hylde, bliver manualerne
          stående</strong> - de kommer bare til at stå uden hylde.
        </p>

        {problem && <div className="error-box" style={{ marginBottom: 12 }}>{problem}</div>}

        {list.length === 0 && <EmptyState>Der er ingen hylder endnu.</EmptyState>}

        <div className="stack" style={{ gap: 2 }}>
          {list.map((category, index) => (
            <div
              key={category.id}
              className="row row--between device-row"
              style={{ gap: 10, opacity: category.isActive ? 1 : 0.55 }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                {editing === category.id ? (
                  <div className="row" style={{ gap: 6 }}>
                    <input
                      autoFocus
                      style={{ maxWidth: 260 }}
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          void rename(category)
                        }
                        if (event.key === 'Escape') setEditing(null)
                      }}
                    />
                    <button className="btn btn--sm btn--primary" onClick={() => void rename(category)}>
                      Gem
                    </button>
                    <button className="btn btn--sm" onClick={() => setEditing(null)}>
                      Fortryd
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={{ fontWeight: 600 }}>
                      {category.name}
                      {!category.isActive && <span className="tiny muted"> · skjult</span>}
                    </div>
                    <div className="tiny muted">
                      {category.manualCount === 0
                        ? 'Ingen manualer'
                        : `${category.manualCount} ${category.manualCount === 1 ? 'manual' : 'manualer'}`}
                    </div>
                  </>
                )}
              </div>

              {isAdmin && editing !== category.id && (
                <div className="btn-group">
                  <button
                    className="btn btn--sm"
                    disabled={index === 0 || save.isPending}
                    title="Flyt op"
                    onClick={() => void move(category, -1)}
                  >
                    ↑
                  </button>
                  <button
                    className="btn btn--sm"
                    disabled={index === list.length - 1 || save.isPending}
                    title="Flyt ned"
                    onClick={() => void move(category, 1)}
                  >
                    ↓
                  </button>
                  <button
                    className="btn btn--sm"
                    onClick={() => {
                      setDraft(category.name)
                      setEditing(category.id)
                    }}
                  >
                    Omdøb
                  </button>
                  <button className="btn btn--sm" onClick={() => void toggleActive(category)}>
                    {category.isActive ? 'Skjul' : 'Vis'}
                  </button>
                  <button className="btn btn--sm" onClick={() => void drop(category)}>
                    Fjern
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 14 }}>
          {adding === null ? (
            <button className="btn btn--primary btn--sm" onClick={() => setAdding('')}>
              Ny hylde
            </button>
          ) : (
            <div className="row" style={{ gap: 6 }}>
              <input
                autoFocus
                style={{ maxWidth: 260 }}
                placeholder="Fx Nødbelysning"
                value={adding}
                onChange={(event) => setAdding(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    void create()
                  }
                  if (event.key === 'Escape') setAdding(null)
                }}
              />
              <button
                className="btn btn--sm btn--primary"
                disabled={!adding.trim() || save.isPending}
                onClick={() => void create()}
              >
                Opret
              </button>
              <button className="btn btn--sm" onClick={() => setAdding(null)}>
                Fortryd
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
